import { DEFAULT_CONFIG } from '../config.js';
import { calculateAllocation } from '../sizing.js';
/** Thin wrapper around V1's `calculateAllocation` — the unmodified, ported quarter-Kelly policy. */
export const standardSizingPolicy = (input, config = DEFAULT_CONFIG) => calculateAllocation(input.recommendation, input.probability, input.contractPrice, input.bankroll, config);
export const DEFAULT_CONFIDENCE_SCALED_OPTIONS = {
    threshold: 0.9,
    capMultiplier: 2,
};
/**
 * EXPERIMENTAL, off by default anywhere in the app — do not switch a live
 * signal to this without first checking `highConfidenceReliability()` on
 * real backtested data for the `>= threshold` bucket (see reliability.ts).
 * If that bucket's *actual* win rate doesn't genuinely support its stated
 * confidence, this makes the model's biggest bets exactly where it is least
 * trustworthy — see v2/README.md, "On confidence-scaled sizing".
 *
 * Otherwise identical to `standardSizingPolicy`, except the max-buy cap is
 * multiplied by `capMultiplier` once probability reaches `threshold`. Kept
 * as its own named policy (not a config flag on the standard one) so a
 * caller always has to explicitly opt into it by name.
 */
export function confidenceScaledSizingPolicy(options = {}) {
    const opts = { ...DEFAULT_CONFIDENCE_SCALED_OPTIONS, ...options };
    return (input, config = DEFAULT_CONFIG) => {
        const effectiveConfig = input.probability >= opts.threshold
            ? { ...config, maxIndividualBuy: config.maxIndividualBuy * opts.capMultiplier }
            : config;
        return calculateAllocation(input.recommendation, input.probability, input.contractPrice, input.bankroll, effectiveConfig);
    };
}
export const DEFAULT_PROFIT_SCALED_TIERS = [
    { profitThreshold: 100, capMultiplier: 1.5 },
    { profitThreshold: 300, capMultiplier: 2 },
];
export const DEFAULT_PROFIT_SCALED_OPTIONS = {
    startingPool: DEFAULT_CONFIG.simulatedStartingPool,
    tiers: DEFAULT_PROFIT_SCALED_TIERS,
};
function capMultiplierForProfit(profit, tiers) {
    let multiplier = 1;
    let bestThreshold = -Infinity;
    for (const tier of tiers) {
        if (profit >= tier.profitThreshold && tier.profitThreshold > bestThreshold) {
            bestThreshold = tier.profitThreshold;
            multiplier = tier.capMultiplier;
        }
    }
    return multiplier;
}
/**
 * EXPERIMENTAL, off by default anywhere in the app — see "On profit-scaled
 * sizing" in v2/README.md before ever switching a live replay to this.
 *
 * Otherwise identical to `standardSizingPolicy`, except the max-buy cap is
 * multiplied by whichever tier's `capMultiplier` the current bankroll's
 * profit (relative to `startingPool`) has reached — "start with a normal
 * cap, raise it once a profit milestone is cleared." Kept as its own named
 * policy (not a config flag on the standard one) so a caller always has to
 * explicitly opt into it by name, same convention as
 * `confidenceScaledSizingPolicy`.
 *
 * Quarter-Kelly sizing is already proportional to the *current* bankroll
 * (`stake = bankroll * appliedFraction`), so raw stakes already grow as the
 * pool compounds — this only changes where the flat cap that raw stake gets
 * clipped against sits, once profit crosses a configured milestone.
 */
export function profitScaledSizingPolicy(options = {}) {
    const opts = { ...DEFAULT_PROFIT_SCALED_OPTIONS, ...options };
    return (input, config = DEFAULT_CONFIG) => {
        const profit = input.bankroll - opts.startingPool;
        const multiplier = capMultiplierForProfit(profit, opts.tiers);
        const effectiveConfig = multiplier === 1
            ? config
            : { ...config, maxIndividualBuy: config.maxIndividualBuy * multiplier };
        return calculateAllocation(input.recommendation, input.probability, input.contractPrice, input.bankroll, effectiveConfig);
    };
}
export const DEFAULT_MARTINGALE_OPTIONS = {
    lossMultiplier: 2,
    maxMultiplier: 16,
};
/**
 * EXPERIMENTAL, off by default anywhere in the app, and never part of
 * `DEFAULT_SHADOW_SIZING_POLICIES` — see "On martingale sizing" in
 * v2/README.md before ever using this for anything beyond a one-off backtest.
 *
 * The classic martingale progression: after each consecutive *loss*, the
 * max-buy cap is multiplied by `lossMultiplier` (default 2 — "double after
 * every loss"), so a large enough win eventually recovers every prior loss
 * in the streak plus a profit equal to the original stake. A win resets the
 * streak and the cap back to 1x. `maxMultiplier` hard-caps how far the
 * streak can scale the cap — real martingale has no such ceiling, which is
 * precisely why it fails: a long enough losing streak demands a bet size no
 * finite bankroll can cover.
 *
 * When the caller passes `input.lossStreak`, that streak is used as given
 * and the call is pure: nothing is inferred or remembered. The shadow
 * replays always pass it, so a bust (a top-up that starts a new run) starts
 * the streak over explicitly.
 *
 * Without it, unlike `confidenceScaledSizingPolicy`/`profitScaledSizingPolicy`,
 * this policy is **stateful**: it infers the previous round's win/loss from
 * the change in `bankroll` between consecutive calls. That means **a fresh
 * instance must be constructed for every independent replay** — call
 * `martingaleSizingPolicy()` again immediately before each backtest rather
 * than reusing one instance across replays or requests. A shared instance
 * would carry a losing streak over from whatever replay last used it,
 * silently corrupting the next one. This is also why it is never added to
 * `DEFAULT_SHADOW_SIZING_POLICIES` (a module-level singleton list reused
 * across every request) — callers that want to shadow it must build and
 * pass their own fresh instance.
 */
export function martingaleSizingPolicy(options = {}) {
    const opts = { ...DEFAULT_MARTINGALE_OPTIONS, ...options };
    let lastBankroll = null;
    let lossStreak = 0;
    return (input, config = DEFAULT_CONFIG) => {
        const streak = input.lossStreak ?? inferStreak(input.bankroll);
        const multiplier = Math.min(opts.lossMultiplier ** streak, opts.maxMultiplier);
        const effectiveConfig = multiplier === 1
            ? config
            : { ...config, maxIndividualBuy: config.maxIndividualBuy * multiplier };
        return calculateAllocation(input.recommendation, input.probability, input.contractPrice, input.bankroll, effectiveConfig);
    };
    function inferStreak(bankroll) {
        if (lastBankroll !== null) {
            if (bankroll < lastBankroll) {
                lossStreak += 1;
            }
            else if (bankroll > lastBankroll) {
                lossStreak = 0;
            }
            // An unchanged bankroll (no trade taken) leaves the streak as-is.
        }
        lastBankroll = bankroll;
        return lossStreak;
    }
}
//# sourceMappingURL=sizingPolicies.js.map